/**
 * 네이티브 앱(WebView) 환경 감지 및 브릿지 API 래퍼.
 *
 * - `isNativeApp`: WebView 안에서 실행 중인지 여부
 * - `nativeBridge.system.*`: SystemBridge 래퍼 (haptic, splash, colorScheme, version)
 * - `nativeBridge.auth.*`: AuthBridge 래퍼 (세션 저장/조회/삭제)
 * - `nativeBridge.ad.*`: AdBridge 래퍼 (배너/인터스티셜 광고 제어)
 */

/* ---------- 환경 감지 ---------- */

export const isNativeApp = typeof window !== "undefined" && Boolean(window.ReactNativeWebView);

/* ---------- 타입 ---------- */

type HapticStyle = "light" | "medium" | "heavy" | "success" | "warning" | "error" | "selection";

interface SystemBridgeAPI {
  getColorScheme: () => Promise<"light" | "dark">;
  getAppVersion: () => Promise<string>;
  triggerHaptic: (style: HapticStyle) => void;
  hideSplashScreen: () => Promise<boolean>;
  onForegrounded: (() => void) | null;
  onColorSchemeChange: ((scheme: string) => void) | null;
}

interface AuthBridgeAPI {
  getSession: () => Promise<{ accessToken: string; refreshToken: string } | null>;
  saveSession: (accessToken: string, refreshToken: string) => void;
  clearSession: () => void;
  signInWithOAuth: (provider: string) => Promise<{ accessToken: string; refreshToken: string } | null>;
}

/** RevenueCat 구독 상품 패키지 (네이티브에서 직렬화하여 전달) */
export interface PurchasePackage {
  identifier: string;
  packageType: string;
  product: {
    identifier: string;
    title: string;
    description: string;
    priceString: string;
    price: number;
    currencyCode: string;
  };
}

/** RevenueCat Offering (구독 상품 그룹) */
export interface PurchaseOffering {
  identifier: string;
  packages: PurchasePackage[];
}

/** 구매/복원 결과 */
export interface PurchaseResult {
  isPlus?: boolean;
  cancelled?: boolean;
}

interface SpeechBridgeAPI {
  speak: (text: string, lang?: string) => void;
  stop: () => void;
  isAvailable: () => boolean;
}

interface AdBridgeAPI {
  /** 인터스티셜(전면) 광고 표시 — 닫힘 시 resolve */
  showInterstitial: () => Promise<{ shown: boolean }>;
  /** 보상형 광고 표시 — 끝까지 시청 시 rewarded: true */
  showRewarded: () => Promise<{ rewarded: boolean }>;
  /** 광고 SDK 초기화 상태 */
  isReady: () => Promise<boolean>;
}

interface PurchaseBridgeAPI {
  logIn: (userId: string) => Promise<void>;
  logOut: () => Promise<void>;
  getOfferings: () => Promise<PurchaseOffering | null>;
  purchase: (packageId: string) => Promise<PurchaseResult>;
  restorePurchases: () => Promise<PurchaseResult>;
  getCustomerInfo: () => Promise<{ isPlus: boolean }>;
  onCustomerInfoUpdated: ((info: { isPlus: boolean }) => void) | null;
}

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (msg: string) => void };
    SystemBridge?: SystemBridgeAPI;
    AuthBridge?: AuthBridgeAPI;
    SpeechBridge?: SpeechBridgeAPI;
    PurchaseBridge?: PurchaseBridgeAPI;
    AdBridge?: AdBridgeAPI;
  }
}

/* ---------- 래퍼 ---------- */

export const nativeBridge = {
  system: {
    haptic(style: HapticStyle = "medium") {
      if (isNativeApp) {
        window.SystemBridge?.triggerHaptic(style);
      }
    },
    async hideSplash() {
      if (isNativeApp) {
        return window.SystemBridge?.hideSplashScreen();
      }
    },
    async getColorScheme() {
      if (isNativeApp) {
        return window.SystemBridge?.getColorScheme();
      }

      return undefined;
    },
    async getAppVersion() {
      if (isNativeApp) {
        return window.SystemBridge?.getAppVersion();
      }

      return undefined;
    },
  },

  auth: {
    saveSession(accessToken: string, refreshToken: string) {
      if (isNativeApp) {
        window.AuthBridge?.saveSession(accessToken, refreshToken);
      }
    },
    clearSession() {
      if (isNativeApp) {
        window.AuthBridge?.clearSession();
      }
    },
    async getSession() {
      if (isNativeApp) {
        return window.AuthBridge?.getSession() ?? null;
      }

      return null;
    },
    async signInWithOAuth(provider: string) {
      if (isNativeApp) {
        return window.AuthBridge?.signInWithOAuth(provider) ?? null;
      }

      return null;
    },
  },
  purchase: {
    async logIn(userId: string) {
      if (isNativeApp) {
        await window.PurchaseBridge?.logIn(userId);
      }
    },
    async logOut() {
      if (isNativeApp) {
        try {
          await window.PurchaseBridge?.logOut();
        } catch {
          // RevenueCat은 익명(미식별) 유저 상태에서 logOut을 호출하면 에러를 던진다.
          // (예: 앱 부팅 시 onAuthStateChange가 세션 없는 상태로 발화 → logOut 호출)
          // 이 케이스는 무해하므로 무시한다. (관련 Sentry: TOBO-20)
        }
      }
    },
    async getOfferings() {
      if (isNativeApp) {
        return window.PurchaseBridge?.getOfferings() ?? null;
      }

      return null;
    },
    async purchase(packageId: string): Promise<PurchaseResult | null> {
      if (isNativeApp) {
        return window.PurchaseBridge?.purchase(packageId) ?? null;
      }

      return null;
    },
    async restorePurchases(): Promise<PurchaseResult | null> {
      if (isNativeApp) {
        return window.PurchaseBridge?.restorePurchases() ?? null;
      }

      return null;
    },
    async getCustomerInfo() {
      if (isNativeApp) {
        return window.PurchaseBridge?.getCustomerInfo() ?? null;
      }

      return null;
    },
  },

  ad: {
    async showInterstitial(): Promise<{ shown: boolean }> {
      if (isNativeApp) {
        return window.AdBridge?.showInterstitial() ?? { shown: false };
      }

      return { shown: false };
    },
    async showRewarded(): Promise<{ rewarded: boolean }> {
      if (isNativeApp) {
        return window.AdBridge?.showRewarded?.() ?? { rewarded: false };
      }

      return { rewarded: false };
    },
    async isReady(): Promise<boolean> {
      if (isNativeApp) {
        return window.AdBridge?.isReady() ?? false;
      }

      return false;
    },
  },
} as const;
